import React from 'react';
import { motion } from 'framer-motion';
import { BookOpenIcon, GraduationCapIcon, MicIcon, AwardIcon, SparklesIcon, StarIcon, ArrowRightIcon } from 'lucide-react';
const sources = ['Harvard Business School', 'Wharton Executive Education', 'INSEAD', 'Stanford GSB', 'MIT Sloan', 'Kellogg', 'Oxford Saïd', 'IMD Lausanne'];
const featuredModules = [{
  title: 'Radical Candor for Leadership Teams',
  author: 'Adapted from Kim Scott',
  source: 'Bestselling Author',
  duration: '90 min',
  icon: MicIcon,
  accent: 'bg-brand-coral/10 text-brand-coral'
}, {
  title: 'The Five Dysfunctions of a Team',
  author: 'Patrick Lencioni framework',
  source: 'NYT Bestseller',
  duration: '2.5 hrs',
  icon: BookOpenIcon,
  accent: 'bg-brand-teal/10 text-brand-teal'
}, {
  title: 'Negotiation Under Pressure',
  author: 'Harvard Negotiation Project',
  source: 'Harvard Business School',
  duration: '3 hrs',
  icon: GraduationCapIcon,
  accent: 'bg-brand-navy/10 text-brand-navy'
}, {
  title: 'Strategic Alignment Sprint',
  author: 'Wharton Executive Education',
  source: 'Wharton',
  duration: '4 hrs',
  icon: AwardIcon,
  accent: 'bg-amber-100 text-amber-700'
}];
export function ContentPartners() {
  return <section className="bg-slate-50 py-24 px-6 md:px-10 border-y border-slate-100" data-id="element-573">
      <div className="max-w-7xl mx-auto" data-id="element-574">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-575">
          <div className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-full px-3.5 py-1.5 text-xs font-bold text-brand-navy uppercase tracking-wider mb-5 shadow-sm" data-id="element-576">
            <SparklesIcon className="w-3.5 h-3.5 text-brand-coral" data-id="element-577" />
            World-Class Content Library
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-brand-navy mb-5 tracking-tight" data-id="element-578">
            The world's best leadership content,
            <br data-id="element-579" />
            <span className="text-brand-teal" data-id="element-580">curated for your offsite.</span>
          </h2>
          <p className="text-lg text-slate-600 leading-relaxed" data-id="element-581">
            Every Offsite+ pathway is built from licensed and adapted material
            from top business schools, bestselling authors, and the facilitators
            leadership teams actually want in the room.
          </p>
        </div>

        {/* Source logos / wordmarks */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-soft px-6 md:px-10 py-8 mb-14" data-id="element-582">
          <div className="text-center mb-6" data-id="element-583">
            <span className="text-xs font-bold text-slate-400 uppercase tracking-[0.2em]" data-id="element-584">
              Sourced From
            </span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-6" data-id="element-585">
            {sources.map((src, i) => <motion.div key={src} initial={{
            opacity: 0,
            y: 8
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.04,
            duration: 0.4
          }} className="text-center" data-id="element-586">
                <div className="font-serif text-base md:text-lg text-brand-navy/80 font-medium leading-tight" data-id="element-587">
                  {src}
                </div>
              </motion.div>)}
          </div>
        </div>

        {/* Featured modules */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5 mb-14" data-id="element-588">
          {featuredModules.map((mod, i) => {
          const Icon = mod.icon;
          return <motion.div key={mod.title} initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.08,
            duration: 0.5
          }} className="bg-white rounded-2xl border border-slate-100 shadow-soft p-6 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 flex flex-col" data-id="element-589">
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-4 ${mod.accent}`} data-id="element-590">
                  <Icon className="w-5 h-5" data-id="element-591" />
                </div>
                <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1.5" data-id="element-592">
                  {mod.source}
                </div>
                <h3 className="text-lg font-bold text-brand-navy mb-2 leading-snug" data-id="element-593">
                  {mod.title}
                </h3>
                <p className="text-sm text-slate-500 mb-5 flex-1" data-id="element-594">
                  {mod.author}
                </p>
                <div className="flex items-center justify-between pt-4 border-t border-slate-100" data-id="element-595">
                  <span className="text-xs font-semibold text-slate-500" data-id="element-596">
                    {mod.duration}
                  </span>
                  <div className="flex items-center gap-0.5 text-amber-500" data-id="element-597">
                    {[...Array(5)].map((_, j) => <StarIcon key={j} className="w-3 h-3 fill-current" data-id="element-598" />)}
                  </div>
                </div>
              </motion.div>;
        })}
        </div>

        {/* Bottom strip: stats + CTA */}
        <div className="bg-brand-navy rounded-2xl p-8 md:p-10 text-white relative overflow-hidden" data-id="element-599">
          <div className="absolute -top-20 -right-20 w-64 h-64 bg-brand-teal/20 rounded-full blur-3xl" data-id="element-600" />
          <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-brand-coral/10 rounded-full blur-3xl" data-id="element-601" />
          <div className="relative z-10 grid md:grid-cols-4 gap-8 items-center" data-id="element-602">
            <div className="md:col-span-1" data-id="element-603">
              <div className="text-4xl font-bold text-white" data-id="element-604">400+</div>
              <div className="text-sm text-slate-300 mt-1" data-id="element-605">
                Licensed modules
              </div>
            </div>
            <div className="md:col-span-1" data-id="element-606">
              <div className="text-4xl font-bold text-brand-teal" data-id="element-607">60+</div>
              <div className="text-sm text-slate-300 mt-1" data-id="element-608">
                Authors & faculty
              </div>
            </div>
            <div className="md:col-span-1" data-id="element-609">
              <div className="text-4xl font-bold text-brand-coral" data-id="element-610">12</div>
              <div className="text-sm text-slate-300 mt-1" data-id="element-611">
                Top-tier business schools
              </div>
            </div>
            <div className="md:col-span-1 md:text-right" data-id="element-612">
              <a href="#" className="inline-flex items-center gap-2 bg-white text-brand-navy font-semibold px-5 py-3 rounded-xl hover:bg-slate-100 transition-colors" data-id="element-613">
                Browse the library
                <ArrowRightIcon className="w-4 h-4" data-id="element-614" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>;
}